/**
 * Vuex módulo para el estado de la estación.
 * Maneja datos, errores, caching y llamadas a API con retry y cancelación.
 */

import axios from '../plugins/axios'

const state = {
  station: null,
  statistics: null,
  packets: [],
  loading: false,
  error: null,
  cache: new Map(),
  abortControllers: {}
}

const mutations = {
  SET_STATION(state, station) {
    state.station = station
  },
  SET_STATISTICS(state, statistics) {
    state.statistics = statistics
  },
  SET_PACKETS(state, packets) {
    state.packets = packets
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  ADD_TO_CACHE(state, { key, data }) {
    state.cache.set(key, data)
  },
  SET_ABORT_CONTROLLER(state, { key, controller }) {
    state.abortControllers[key] = controller
  },
  CLEAR_ABORT_CONTROLLER(state, key) {
    delete state.abortControllers[key]
  }
}

const actions = {
  /**
   * Obtiene los datos de la estación con caching, retry y cancelación.
   * @param {Object} context - Contexto de Vuex.
   * @param {String} stationId - ID de la estación.
   */
  async fetchStation({ commit, state }, stationId) {
    const cacheKey = `station_${stationId}`
    if (state.cache.has(cacheKey)) {
      commit('SET_STATION', state.cache.get(cacheKey))
      return
    }
    let retries = 2
    let controller = new AbortController()
    commit('SET_ABORT_CONTROLLER', { key: cacheKey, controller })
    commit('SET_LOADING', true)
    while (retries >= 0) {
      try {
        const { data } = await axios.get(
          `/v1/station/${stationId}`,
          { signal: controller.signal }
        )
        if (!data.description) data.description = ""
        if (!data.pictures) data.pictures = []
        data.id = stationId
        commit('SET_STATION', data)
        commit('ADD_TO_CACHE', { key: cacheKey, data })
        commit('SET_ERROR', null)
        commit('CLEAR_ABORT_CONTROLLER', cacheKey)
        commit('SET_LOADING', false)
        return
      } catch (err) {
        if (axios.isCancel(err)) {
          commit('SET_ERROR', 'Request cancelled')
          break
        }
        if (err.code === 'ECONNABORTED') {
          commit('SET_ERROR', 'Request timeout - trying again...')
        } else {
          commit('SET_ERROR', err.message)
        }
        retries--
      }
    }
    commit('SET_LOADING', false)
    commit('CLEAR_ABORT_CONTROLLER', cacheKey)
  },

  /**
   * Cancela la petición de estación si está activa.
   * @param {Object} context - Contexto de Vuex.
   * @param {String} stationId - ID de la estación.
   */
  cancelFetchStation({ state, commit }, stationId) {
    const cacheKey = `station_${stationId}`
    const controller = state.abortControllers[cacheKey]
    if (controller) {
      controller.abort()
      commit('CLEAR_ABORT_CONTROLLER', cacheKey)
    }
  }
}

const getters = {
  station: state => state.station,
  statistics: state => state.statistics,
  packets: state => state.packets,
  loading: state => state.loading,
  error: state => state.error
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}